import type { SetCopy } from '../catalog'

// Founders: a minifigure of the founder, made only from stock parts picked
// to look like them, in a small scene with a display-sized build of the
// thing they made.
export const FOUNDERS: SetCopy[] = [
  {
    slug: 'palmer-luckey',
    series: 'founders',
    number: 1,
    name: 'Palmer Luckey',
    maker: 'Anduril',
    year: 2023,
    tagline: 'A Hawaiian shirt and a jet that lands on its tail.',
    story:
      'Palmer, in a teal Hawaiian shirt, stands by a field case with a laptop and a VR headset, next to an Anduril Roadrunner standing on its tail on the pad. The 24 cm airframe is the Frontier set’s: a flat delta skinned front and back on sideways studs, its leading edge one straight line of wing plates, lime wing tips, a round forebody with a yellow band and a silver dome.',
    age: '10+',
    palette: {
      cover: '#2A2E2B',
      flood: '#1C1F1D',
      ink: '#E9F5C9',
      ground: '#D9DCD6',
      accent: '#A5CA18',
    },
    aliases: ['palmer luckey', 'anduril', 'roadrunner', 'oculus', 'drone'],
  },
  {
    slug: 'steve-jobs',
    series: 'founders',
    number: 2,
    name: 'Steve Jobs',
    maker: 'Apple',
    year: 1984,
    tagline: 'Black turtleneck, round glasses, one beige Mac.',
    story:
      'Jobs, in his black turtleneck, stands at a walnut desk beside a Macintosh 128K about half real size. Behind the bezel its case steps in and curves away at every edge, with the handle slot cut into the top. The screen shows the Finder as a tile mosaic on sideways studs, and six coloured plates make the rainbow logo. The keyboard in front has a tile for every key, and there is a mouse and a stack of floppies.',
    age: '10+',
    palette: {
      cover: '#E8DCC2',
      flood: '#5F3109',
      ink: '#F6EFE2',
      ground: '#EFE7D6',
      accent: '#1B1B1B',
    },
    aliases: ['steve jobs', 'jobs', 'macintosh', 'mac', 'apple founder'],
  },
  {
    slug: 'steve-wozniak',
    series: 'founders',
    number: 3,
    name: 'Steve Wozniak',
    maker: 'Apple',
    year: 1976,
    tagline: 'The Apple I, in a wooden box.',
    story:
      'Woz, bearded, stands by an Apple I in the kind of koa wood case its first owners built for it. The open top shows the green board, its rows of chips, the blue capacitors and the heat sink. The keyboard has one tile per key. Beside it, a small monitor shows the Woz Monitor prompt in green on black.',
    age: '10+',
    palette: {
      cover: '#D7BA8C',
      flood: '#5F3109',
      ink: '#FFF4E0',
      ground: '#EFE4CF',
      accent: '#00852B',
    },
    aliases: ['woz', 'wozniak', 'apple i', 'apple 1', 'homebrew'],
  },
  {
    slug: 'bill-gates',
    series: 'founders',
    number: 4,
    name: 'Bill Gates',
    maker: 'Microsoft',
    year: 1975,
    tagline: 'A green sweater, big glasses and an Altair.',
    story:
      'A young Gates and Paul Allen stand beside the MITS Altair 8800 and the Teletype they loaded their first BASIC from. The Altair is 22 cm wide in a blue cover. Its front panel of red lights and toggle switches is round tiles on sideways studs. The Teletype has its round keys and paper roll, and yellow paper tape trails across the floor.',
    age: '10+',
    palette: {
      cover: '#7396C8',
      flood: '#1E3A6B',
      ink: '#F0F4FA',
      ground: '#E4E8EE',
      accent: '#B40000',
    },
    aliases: ['bill gates', 'paul allen', 'microsoft', 'altair', 'teletype'],
  },
  {
    slug: 'jensen-huang',
    series: 'founders',
    number: 5,
    name: 'Jensen Huang',
    maker: 'Nvidia',
    year: 1993,
    tagline: 'Leather jacket, glasses, a very large graphics card.',
    story:
      'Jensen, in his black leather jacket, stands on a keynote stage beside a GeForce graphics card 27 cm long, nine tenths of real size, standing on its edge in a cradle. Its face is built flat and stood up on side-stud bricks: a silver frame three plates deep round two recessed fan wells with rounded corners, a round plate and a dish for each fan, and grille tiles down the fin stack between them. The lit GEFORCE strip runs along the top, the gold fingers along the bottom and the I/O bracket at the end; the back is a smooth black plate.',
    age: '10+',
    palette: {
      cover: '#1B1B1B',
      flood: '#0E0E0E',
      ink: '#DDF3B0',
      ground: '#DADDD6',
      accent: '#76B900',
    },
    aliases: ['jensen huang', 'nvidia', 'gpu', 'graphics card', 'geforce'],
  },
  {
    slug: 'mark-zuckerberg',
    series: 'founders',
    number: 6,
    name: 'Mark Zuckerberg',
    maker: 'Meta',
    year: 2021,
    tagline: 'A grey tee and the infinity loop.',
    story:
      'Zuckerberg, in his grey tee, stands on the Connect stage in front of the Meta mark, 32 cm across. The loop is drawn the way tile logos are, built flat with its studs facing you and stood up on posts hidden behind its lower curves: two crossed plate courses in dark blue, then a face that runs from blue to azure, every outside corner a quarter-round tile. A Quest headset stands on a pedestal beside him.',
    age: '10+',
    palette: {
      cover: '#E9EEF6',
      flood: '#0866FF',
      ink: '#FFFFFF',
      ground: '#EEF2F8',
      accent: '#1E5AA8',
    },
    aliases: ['mark zuckerberg', 'zuckerberg', 'meta', 'facebook', 'infinity'],
  },
  {
    slug: 'page-brin',
    series: 'founders',
    number: 7,
    name: 'Page & Brin',
    maker: 'Google',
    year: 1998,
    tagline: 'A garage, a ping-pong table and a drive case made of toy bricks.',
    story:
      'Larry Page and Sergey Brin in the Menlo Park garage Google rented in 1998, open to the front like LEGO’s room sets: bright blue carpet, beige PCs on a desk and on a door across trestles, a ping-pong table, and the wordmark standing on the back wall in its four colours, each letter drawn in plan with rounded corners. In the corner is the storage box they built at Stanford in 1996 to hold ten 4 GB drives: a closed case of coloured toy bricks on a white board, two black fan grilles on its front on sideways studs, grey down one side and two little figures on top.',
    age: '10+',
    palette: {
      cover: '#F1F3F4',
      flood: '#4285F4',
      ink: '#FFFFFF',
      ground: '#EEF0F2',
      accent: '#34A853',
    },
    aliases: ['larry page', 'sergey brin', 'google', 'garage', 'lego server'],
  },
  {
    slug: 'sam-altman',
    series: 'founders',
    number: 8,
    name: 'Sam Altman',
    maker: 'OpenAI',
    year: 2022,
    tagline: 'A navy sweater and the blossom.',
    story:
      'Altman, in a plain navy sweater, stands in an OpenAI office on the night ChatGPT launched, a room open to the front like LEGO’s own room sets. The blossom stands proud of the white back wall in black relief, drawn in plan with every outside corner rounded and its six seams in grey; wood slats run behind a grey sofa, a whiteboard hangs on the side wall, and the walls are tiled on sideways studs. His laptop is open to ChatGPT; a colleague works at a monitor beside him, on a plank floor with plants in the corners.',
    age: '10+',
    palette: {
      cover: '#F4F4F4',
      flood: '#101010',
      ink: '#F4F4F4',
      ground: '#EDEDED',
      accent: '#10A37F',
    },
    aliases: ['sam altman', 'altman', 'openai', 'chatgpt', 'blossom'],
  },
  {
    slug: 'collison-brothers',
    series: 'founders',
    number: 9,
    name: 'Collison Brothers',
    maker: 'Stripe',
    year: 2010,
    tagline: 'Two brothers from Tipperary and their name in lights.',
    story:
      "Patrick and John Collison, the red-haired brothers from Tipperary who started Stripe in 2010, on the Stripe Sessions stage. Behind them an LED wall shows Stripe's navy and its gradient ribbon, with the lowercase wordmark standing proud of it in white, the letters' bowls rounded with quarter-round tiles. Two armchairs for the fireside chat, a clear lectern, floor monitors and truss towers with lamps turned on the stage.",
    age: '10+',
    palette: {
      cover: '#E9E7FF',
      flood: '#635BFF',
      ink: '#FFFFFF',
      ground: '#F1F0FA',
      accent: '#441A91',
    },
    aliases: [
      'patrick collison',
      'john collison',
      'collison',
      'stripe',
      'payments',
    ],
  },
  {
    slug: 'jeff-bezos',
    series: 'founders',
    number: 10,
    name: 'Jeff Bezos',
    maker: 'Amazon',
    year: 1995,
    tagline: 'Door desks, a stove and the first boxes.',
    story:
      'Bezos, in a white shirt and khakis, stands in the Bellevue garage where Amazon started. The two door desks are plain wooden doors on legs, one with a round tile where its knob used to be. There is a bookcase of the only thing Amazon sold at first, and the pot-bellied stove that heated the garage. In front stands a shipping box with the smile across it.',
    age: '10+',
    palette: {
      cover: '#E3D4B8',
      flood: '#232F3E',
      ink: '#F3E7CF',
      ground: '#EDE5D6',
      accent: '#FF9900',
    },
    aliases: ['jeff bezos', 'bezos', 'amazon', 'door desk', 'garage'],
  },
  {
    slug: 'elon-musk',
    series: 'founders',
    number: 11,
    name: 'Elon Musk',
    maker: 'SpaceX',
    year: 2015,
    tagline: 'A rocket booster, back on its legs.',
    story:
      'Musk, in a dark suit, stands on a droneship deck beside a Falcon 9 first stage that has just landed. The booster is 44 cm tall and round the way display rockets are: curved slopes on sideways plates, pinwheeled round a core of bricks with studs on their sides, sooty from the landing burn, with a black raceway up one corner. Four legs swing out on locking hinges to clip plates on the landing target, nine engine bells hang under the octaweb, and grid fins cross the black interstage.',
    age: '10+',
    palette: {
      cover: '#2B2F33',
      flood: '#0B0D10',
      ink: '#EDEFF2',
      ground: '#D8DBDF',
      accent: '#F4F4F4',
    },
    aliases: ['elon musk', 'musk', 'spacex', 'falcon 9', 'rocket landing'],
  },
  {
    slug: 'paypal-mafia',
    series: 'founders',
    number: 12,
    name: 'PayPal Mafia',
    maker: 'PayPal',
    year: 2007,
    tagline: 'One booth at Tosca, and a poker game on the table.',
    story:
      'Fortune photographed the PayPal Mafia in 2007 at Tosca, the old North Beach bar in San Francisco. Here are five of them: Peter Thiel in a dark suit, Reid Hoffman beside him, Max Levchin, David Sacks and Keith Rabois, round a red leather booth with poker chips, cards and whisky on the table. The booth is channel-tufted in round bricks, the mural sits in a raised gold frame over dark wood panelling, and the PayPal P hangs small on the side wall. Elon Musk isn’t in the photo or the set.',
    age: '10+',
    palette: {
      cover: '#E6EEF8',
      flood: '#003087',
      ink: '#FFFFFF',
      ground: '#EEF2F7',
      accent: '#009CDE',
    },
    aliases: [
      'paypal mafia',
      'paypal',
      'peter thiel',
      'reid hoffman',
      'max levchin',
      'david sacks',
      'keith rabois',
      'tosca',
    ],
  },
  {
    slug: 'tesla',
    series: 'founders',
    number: 13,
    name: 'Tesla',
    maker: 'Tesla',
    year: 2003,
    tagline: 'The whole line-up, Roadster to Semi, and Optimus.',
    story:
      'A Tesla showroom in three tiers, with every production model at Speed Champions scale, eight studs wide. On the floor are the 2008 Roadster with its top off, a red Model S with the early black nose cone and a white Model 3. A brick up are the Model X, its falcon-wing doors swung open over the second-row seats, and a white Model Y. On the top tier stand the Semi’s cab over its tandem axles and the Cybertruck, whose roof is two flat planes, each a slab tilted about one end. Every car has smooth doors of tiles on side studs, a black glass greenhouse of curved slopes, and real axles in Technic bricks under arched mudguards. Elon Musk stands at the wall, where the T rises in red relief, its arc turned down at the ends on round-corner tiles, beside Optimus in its champagne-gold Gen 3 colours and a Supercharger.',
    age: '18+',
    palette: {
      cover: '#1C1D1F',
      flood: '#0B0B0C',
      ink: '#F4F4F4',
      ground: '#E4E5E7',
      accent: '#E31937',
    },
    aliases: [
      'tesla motors',
      'tesla cars',
      'model s',
      'model 3',
      'model x',
      'model y',
      'cybertruck',
      'tesla semi',
      'tesla roadster',
      'optimus',
      'electric car',
    ],
  },
]
